import { useState, useEffect } from 'react';
import { Info, Download, FileText } from 'lucide-react';
import { useConfigTab, FORM_STYLES, Toggle, ConfigTimestamps, LoadingState } from './shared';

const WEBHOOK_GUIDE_URL = '/docs/Wayuvega-Webhook-Integration-Guide.pdf';

export function WebhookTab({ merchantId, reloadKey }) {
  const { loading, saving, isNew, configValue, metadata, saveConfig } = useConfigTab(
    merchantId, 'MERCHANT_WEBHOOK_CONFIGURATIONS', reloadKey,
  );

  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');

  useEffect(() => {
    if (!configValue) {
      setWebhookEnabled(false);
      setWebhookUrl('');
      return;
    }
    setWebhookEnabled(configValue.webhook_enabled === true);
    setWebhookUrl(typeof configValue.webhook_url === 'string' ? configValue.webhook_url : '');
  }, [configValue]);

  const handleSave = () => {
    if (webhookEnabled && !webhookUrl.trim()) {
      alert('Webhook URL is required when webhook is enabled');
      return;
    }
    saveConfig({
      merchant_webhook_configurations_value: {
        webhook_enabled: webhookEnabled,
        webhook_url: webhookEnabled ? webhookUrl.trim() : (webhookUrl.trim() || null),
      },
    });
  };

  if (loading) return <LoadingState />;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-3">
        <div className="flex items-start gap-2 px-3 py-2.5 bg-blue-50 border border-blue-200 rounded-lg text-xs sm:text-sm text-blue-800">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Webhook Required for Order Status Updates</p>
            <p className="text-[11px] text-blue-600 mt-0.5">
              Configure your endpoint to receive real-time shipment and delivery events.
            </p>
          </div>
        </div>

        <a
          href={WEBHOOK_GUIDE_URL}
          download="Wayuvega-Webhook-Integration-Guide.pdf"
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-colors shrink-0 self-start"
        >
          <FileText className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">Integration Guide</span>
          <Download className="w-3.5 h-3.5 text-blue-600" />
        </a>
      </div>

      {isNew && (
        <div className="flex items-center gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg text-xs sm:text-sm text-amber-800">
          <span className="font-medium">No existing configuration</span> - fill the form below and save to create one.
        </div>
      )}

      <div className={FORM_STYLES.sectionCard}>
        <h3 className={FORM_STYLES.sectionTitle}>Webhook Settings</h3>
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr] gap-4 items-start">
          <Toggle
            id="webhook-enabled"
            checked={webhookEnabled}
            onChange={setWebhookEnabled}
            label="Webhook Enabled"
            description="Enable outbound event delivery"
          />
          <div>
            <label className={FORM_STYLES.label}>Webhook URL <span className="text-red-500">*</span></label>
            <input
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://api.merchant.com/webhooks/events"
              className={FORM_STYLES.input}
            />
            <p className="text-[11px] text-slate-400 mt-1">The endpoint that will receive OMS event payloads via POST requests.</p>
          </div>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3 border-t border-slate-100">
        <button type="button" onClick={handleSave} disabled={saving} className={FORM_STYLES.btnPrimary}>
          {saving
            ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            : <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          }
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {!isNew && <ConfigTimestamps metadata={metadata} />}
      </div>
    </div>
  );
}
